import React from 'react';
import PercentageBar from '@reverbdotcom/commons/src/components/percentage_bar';
import I18n from 'i18n-js';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { ProductReviewInteraction } from '@reverbdotcom/commons/src/elog/mparticle_types';
import RouterHistory from '@reverbdotcom/commons/src/cms/lib/router_history';
import { useURL } from '@reverbdotcom/commons/src/url_context';
import classNames from 'classnames';

export interface RatingDistribution {
  rating?: string;
  reviewCount?: number;
}

interface Props {
  total: number;
  ratingDistribution: RatingDistribution;
  trackingDisplayStyle: 'product' | 'csp';
  cspId: string;
}

export function ProductReviewsRating(props: Props) {
  const { total, ratingDistribution, trackingDisplayStyle, cspId } = props;
  const url = useURL();

  if (!total || !ratingDistribution) {
    return null;
  }

  const ratingSelected = ratingDistribution.rating === url?.query?.rating;

  function percentageBarColor() {
    if (ratingSelected) {
      return 'dark-gray';
    }

    if (url?.query?.rating) {
      return 'light-gray';
    }

    return null;
  }

  function paramValue() {
    if (ratingSelected) {
      return [];
    }

    return ratingDistribution.rating;
  }

  function filterByRating() {
    const query = {
      ...url.query,
      rating: paramValue(),
    };

    const rating = paramValue().length === 0 ? null : paramValue().toString();

    trackEvent({
      eventName: MParticleEventName.ProductReviewAction,
      productReviewInteraction: ProductReviewInteraction.FilterRating,
      displayStyle: trackingDisplayStyle,
      cspId: cspId,
      rating: rating,
    });

    RouterHistory.push({
      query,
      pathname: url.pathname,
      hash: url.hash,
    });
  }

  const ratingButtonClasses = classNames(
    'button-as-link',
    { 'weight-bold': ratingSelected },
  );

  const ratingCountClasses = classNames(
    { 'weight-bold': ratingSelected },
  );

  return (
    <div className="d-flex mb-2">
      <button
        disabled={ratingDistribution.reviewCount === 0}
        onClick={filterByRating}
        className={ratingButtonClasses}
      >
        {I18n.t('discovery.productReviews.reviewsRating.stars', { count: ratingDistribution.rating })}
      </button>
      <div className="fx-grow mlr-2 mt-2">
        <PercentageBar
          percentage={(ratingDistribution.reviewCount / total) * 100}
          color={percentageBarColor()}
        />
      </div>
      <span className={ratingCountClasses}>
        {I18n.t('discovery.productReviews.reviewsRating.reviews', { count: ratingDistribution.reviewCount })}
      </span>
    </div>
  );
}

export default ProductReviewsRating;
